import api, { ApiResponse } from "./api";
import { Campaign, Media } from "../types/campaign";
import { Template } from "../types/template";
import { PaymentHistory } from "../types/paymentHistory";
import {
    AdminAllPaymentHistory,
    AdminUserPaymentHistory,
    AdminUserPaymentHistoryRecord
} from "../types/adminPaymentHistory";

const API_URL = "v1/admin/";

class AdminService {
    async queryActive() {
        const response = await api
            .post(API_URL + "query/ad/active")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const ads: { [key: string]: any }[] = response.data.v?.ads ?? [];
                    return ads.map(campaign => {
                        const media = new Media(campaign.media.urls, campaign.media.media);
                        return new Campaign(
                            campaign.id,
                            campaign.title,
                            campaign.nickname,
                            campaign.adStatus,
                            campaign.approvalStatus,
                            campaign.adminMsg,
                            media,
                            campaign.startTime,
                            campaign.endTime,
                            1,
                            "25",
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            campaign.templateId,
                        )
                    })
                }

                return [];
            })
            .catch(() => []);
        
        return response;
    }

    async queryPending() {
        const response = await api
            .post(API_URL + "query/ad/pending_approval")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const ads: { [key: string]: any }[] = response.data.v?.ads ?? [];
                    return ads.map(campaign => {
                        const media = new Media(campaign.media.urls, campaign.media.media);
                        return new Campaign(
                            campaign.id,
                            campaign.title,
                            campaign.nickname,
                            campaign.adStatus,
                            campaign.approvalStatus,
                            campaign.adminMsg,
                            media,
                            campaign.startTime,
                            campaign.endTime,
                            1,
                            "25",
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            campaign.templateId,
                        )
                    })
                }

                return [];
            })
            .catch(() => []);
        
        return response;
    }

    async approve(adId: number, message: string) {
        const response = await api
            .post(API_URL + "update/ad/status/approved", {
                adId,
                message,
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
        
        return response;
    }

    async reject(adId: number, message: string) {
        const response = await api
            .post(API_URL + "update/ad/status/rejected", {
                adId,
                message,
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
        
        return response;
    }

    async queryTemplate() {
        const response = await api
            .post(API_URL + "ad/template/query")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const templates: { [key: string]: any }[] = response.data.v ?? [];
                    return templates.map(template => {
                        return new Template(
                            template.id,
                            template.name,
                            template.description,
                            template.contentType,
                            template.transitionType,
                            template.orientation,
                            template.html,
                            template.updatedAt,
                            template.archivedAt,
                            template.uid,
                            template.businessName,
                        )
                    })
                }

                return [];
            })
            .catch(() => []);
        
        return response;
    }

    async createTemplate(
        name: string,
        description: string,
        html: string,
        orientation: string,
        contentType: string,
        transitionType: string,
    ) {
        const response = await api
            .post(API_URL + "ad/template/create", {
                name,
                description,
                html,
                orientation,
                contentType,
                transitionType,
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
            
        return response;
    }

    async updateTemplate(
        id: number,
        name: string,
        description: string,
        html: string,
        orientation: string,
        contentType: string,
        transitionType: string,
    ) {
        const response = await api
            .post(API_URL + "ad/template/update", {
                id,
                name,
                description,
                html,
                orientation,
                contentType,
                transitionType,
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
            
        return response;
    }

    async archiveTemplate(id: number) {
        const response = await api
            .post(API_URL + "ad/template/archive", { id })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
        
        return response;
    }

    async querySucceededPaymentHistory(uid: number, pageIndex: number) {
        const response = await api
            .post(API_URL + "stripe/payment/history/query/status/succeeded", { uid, pageIndex })
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    return new PaymentHistory(
                        response.data.v.totalRecords,
                        response.data.v.records
                    )
                }

                return null;
            })
            .catch(() => null);
        
        return response;
    }

    async queryFailedPaymentHistory(uid: number, pageIndex: number) {
        const response = await api
            .post(API_URL + "stripe/payment/history/query/status/failed", { uid, pageIndex })
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    return new PaymentHistory(
                        response.data.v.totalRecords,
                        response.data.v.records
                    )
                }

                return null;
            })
            .catch(() => null);
        
        return response;
    }

    async queryAllPaymentHistory() {
        const response = await api
            .post(API_URL + "stripe/payment/history/all/query")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const payments: { [key: string]: any }[] = response.data.v ?? [];
                    return payments.map((payment) => {
                        return new AdminAllPaymentHistory(
                            payment.uid,
                            payment.amountTotal,
                            payment.amount,
                            payment.tax,
                            payment.recordCount,
                            payment.businessName,
                            payment.email,
                            payment.status
                        )
                    })
                }

                return [];
            })
            .catch(() => []);
        
        return response;
    }

    async queryUserPaymentHistory(uid: number) {
        const response = await api
            .post(API_URL + "stripe/payment/history/user/query", { uid })
                .then((response: ApiResponse) => {
                    if (response.data.c === 1) {
                        const data: { [key: string]: any }[] = response.data.v?.records ?? [];
                        const records = data.map((record) => {
                            return new AdminUserPaymentHistoryRecord(
                                record.message,
                                record.status,
                                record.stripeStatus,
                                record.amountTotal,
                                record.amount,
                                record.tax,
                                record.created,
                                record.stripeAmountTotal,
                                record.stripeCurrency,
                                record.stripeReceiptUrl,
                                record.stripeCreated
                            )
                        })

                        return new AdminUserPaymentHistory(
                            response.data.v.stripeAmount,
                            response.data.v.dbAmount,
                            records
                        )
                    }

                    return null;
                })
                .catch(() => null);
        
        return response;
    }
}

const adminService = new AdminService();

export default adminService;
